import { thumbnailSize, type Size } from '@oscom/shared';

/** JPEG keeps a scanned plan small; 0.8 stays legible at 480 px. */
const THUMBNAIL_TYPE = 'image/jpeg';
const THUMBNAIL_QUALITY = 0.8;

type DrawingContext = Pick<CanvasRenderingContext2D, 'drawImage' | 'fillRect' | 'fillStyle'>;

interface Surface {
  context: DrawingContext;
  toBlob: () => Promise<Blob>;
}

function createSurface(size: Size): Surface {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(size.width, size.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Kein Zeichenkontext verfügbar.');
    return {
      context,
      toBlob: () => canvas.convertToBlob({ type: THUMBNAIL_TYPE, quality: THUMBNAIL_QUALITY }),
    };
  }
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Kein Zeichenkontext verfügbar.');
  return {
    context,
    toBlob: () =>
      new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Vorschau nicht erzeugt.'))),
          THUMBNAIL_TYPE,
          THUMBNAIL_QUALITY,
        );
      }),
  };
}

/**
 * Small JPEG preview of a plan image, drawn by the browser (which has the file
 * anyway) so the server never decodes a 20 000 px scan. Thin on purpose: the
 * size rule is `thumbnailSize` in shared, tested there.
 */
export async function createThumbnail(file: Blob, source: Size): Promise<Blob> {
  const target = thumbnailSize(source);
  // Decoding with resize options spares memory where the browser supports
  // them; where it ignores them, drawImage below scales just the same, and
  // where it refuses them, the plain decode is the fallback.
  // imageOrientation 'none': the server checks the raw (unrotated) dimensions,
  // so an EXIF-rotated JPEG must not be rotated here or the target size
  // computed from those dimensions would squeeze it.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, {
      imageOrientation: 'none',
      resizeWidth: target.width,
      resizeHeight: target.height,
      resizeQuality: 'high',
    });
  } catch {
    bitmap = await createImageBitmap(file, { imageOrientation: 'none' });
  }
  try {
    const surface = createSurface(target);
    // JPEG has no alpha: without a white ground, transparent areas turn black.
    surface.context.fillStyle = '#ffffff';
    surface.context.fillRect(0, 0, target.width, target.height);
    surface.context.drawImage(bitmap, 0, 0, target.width, target.height);
    return await surface.toBlob();
  } finally {
    bitmap.close();
  }
}
